import { useLingui } from "@lingui/react/macro";
import type { Bot, Group, ThreadSnapshot } from "@rakazo/contracts";
import { BotAvatar, Button, GroupAvatar } from "@rakazo/ui-web";
import { Menu, Pencil, Phone } from "lucide-react";
import { IconTooltip } from "../../components/IconTooltip";

/** The floating header over the transcript: navigation, the chat's name, call and edit. */
export function ChatHeader({
  inGroup,
  active,
  activeGroup,
  activeSnapshot,
  phoneCallsEnabled,
  inCall,
  rightPanelOpen,
  onOpenNavigation,
  onOpenChatSettings,
  onCall,
}: {
  inGroup: boolean;
  active: Bot | undefined;
  activeGroup: Group | undefined;
  activeSnapshot: ThreadSnapshot | null;
  phoneCallsEnabled: boolean;
  inCall: boolean;
  rightPanelOpen: boolean;
  onOpenNavigation: () => void;
  onOpenChatSettings: () => void;
  onCall: (bot: Bot) => void;
}) {
  const { t } = useLingui();
  return (
    <div className="pointer-events-none absolute inset-x-0 top-0 z-10 grid grid-cols-[1fr_minmax(0,auto)_1fr] items-start gap-2 px-3 py-[13px] md:px-[22px] [&_button]:pointer-events-auto">
      <div className="flex items-center">
        <Button
          variant="floating"
          size="icon-lg"
          aria-label={t`Open navigation`}
          onClick={onOpenNavigation}
          className="app-no-drag md:hidden"
        >
          <Menu strokeWidth={1.7} className="size-[19px]" />
        </Button>
      </div>
      <Button
        variant="text"
        data-testid="bot-settings-trigger"
        onClick={onOpenChatSettings}
        className="app-no-drag h-auto min-w-0 flex-col gap-0 rounded-2xl p-0"
      >
        {inGroup || active ? (
          <span className="grid size-14 shrink-0 place-items-center rounded-full border border-border bg-muted/90 shadow-sm backdrop-blur group-hover/button:bg-accent">
            {inGroup ? (
              <GroupAvatar
                members={activeSnapshot?.members ?? activeGroup?.members ?? []}
                size={48}
              />
            ) : active ? (
              <BotAvatar
                avatarSet={active.avatarSet}
                color={active.color}
                identity={active.id}
                size={48}
                status={active.status}
              />
            ) : null}
          </span>
        ) : null}
        <span className="relative -mt-1 max-w-full min-w-0 rounded-full border border-border bg-muted/90 px-3 py-1 backdrop-blur group-hover/button:bg-accent">
          <span className="block truncate text-base font-medium text-foreground" dir="auto">
            {inGroup
              ? (activeGroup?.name ?? activeSnapshot?.groupName ?? t`Group`)
              : (active?.name ?? t`Select a bot`)}
          </span>
        </span>
      </Button>
      <div className="flex items-center justify-self-end gap-2">
        {phoneCallsEnabled && !inGroup && active ? (
          <IconTooltip label={inCall ? t`Open call` : t`Call`}>
            <Button
              variant="floating"
              size="icon-lg"
              data-testid="call-button"
              aria-label={inCall ? t`Open call` : t`Call`}
              onClick={() => onCall(active)}
              data-active={inCall ? "" : undefined}
              className="app-no-drag"
            >
              <Phone strokeWidth={1.6} className="size-[17px]" />
            </Button>
          </IconTooltip>
        ) : null}
        {!rightPanelOpen && (active || activeGroup) ? (
          <IconTooltip label={inGroup ? t`Edit group` : t`Edit bot`}>
            <Button
              variant="floating"
              size="icon-lg"
              data-testid="edit-chat-button"
              aria-label={inGroup ? t`Edit group` : t`Edit bot`}
              onClick={onOpenChatSettings}
              className="app-no-drag"
            >
              <Pencil strokeWidth={1.6} className="size-[17px]" />
            </Button>
          </IconTooltip>
        ) : null}
      </div>
    </div>
  );
}
